import {VNode} from 'snabbdom';

import {FlatSortConfig, SortConfig} from 'common/types/reports/bookmark';
import {PropertyType} from 'common/report/constants';
import {ModifierKeys} from 'common/util/keyboard';
import {LiftType} from 'common/report/util/lift/types';
import {AbsoluteDateRange} from 'common/util/date';
import {Translatable} from 'common/i18n';

export type DataTableAttrs = {
  'multi-select'?: boolean;
  'selection-limit'?: number;
  collapsible?: boolean;
  sorting?: SortConfig;
  'has-comparison'?: boolean;
  'track-name': TrackName;
  theme: Theme;
  'column-width-overrides'?: ColumnWidthOverrides;
  'segmentation-limit': number;
  'resizing-layout'?: boolean;
  'selected-cell'?: CellCoordinates;
  'start-scrolled-right'?: boolean;
  'is-pivot-view'?: boolean;
  'lift-comparison-type'?: LiftType;
  'hovered-series-id'?: string;
  'is-viewn-mode'?: boolean;
  'can-toggle-viewn'?: boolean;
  'aggregation-type'?: AggregationType;
};

export type DataTableParams = {
  columnGroups: Columns;
  rows: TableRow[];
  segmentMetadata?: SegmentMetadataNode;
  selectedRowCount?: number;
  customTrackingProps?: Record<string, any>;
};

export type DataTableProps = {
  dataTableParams: DataTableParams;
};

export type AggregationChangeEvent = CustomEvent<{
  aggregationType: AggregationType;
  sorting?: SortConfig;
}>;

export type DataTableJsxProps = {
  attrs?: DataTableAttrs;
  props: DataTableProps;
  on?: {
    aggregationChange?: (ev: AggregationChangeEvent) => void;
    bodyRowHover?: (ev: BodyRowHoverEvent) => void;
    cellClick?: (ev: CellClickEvent) => void;
    collapseChange?: (ev: CollapseChangeEvent) => void;
    columnResize?: (ev: ColumnResizeEvent) => void;
    selectionChange?: (ev: SelectionChangeEvent) => void;
    sortChange?: (ev: SortChangeEvent) => void;
    tableScroll?: (ev: CustomEvent) => void;
    viewNToggle?: (ev: ViewNToggleEvent) => void;
  };
};

/**
 * High level theme for table styles.
 *
 * hides the actual config options for easier development until they stabilize.
 */
export enum Theme {
  Default = `default`,
  Funnels = `funnels`,
  Standalone = `standalone`,
  Retention = `retention`,
}

export enum AggregationType {
  Average = `average`,
  Sum = `sum`,
  Min = `min`,
  Max = `max`,
  Median = `median`,
}

/**
 * Special keyword column fields that are used internally
 */
export const CommonDataTableFields = {
  Average: `averageValue`,
  Sum: `sumValue`,
  Min: `minValue`,
  Max: `maxValue`,
  Median: `medianValue`,
  TimeComparisonAverage: `tcAverageValue`,
  TimeComparisonSum: `tcSumValue`,
  TimeComparisonMin: `tcMinValue`,
  TimeComparisonMax: `tcMaxValue`,
  TimeComparisonMedian: `tcMedianValue`,
  LiftAverage: `liftAverageValue`,
  LiftSum: `liftSumValue`,
  LiftMin: `liftMinValue`,
  LiftMax: `liftMaxValue`,
  LiftMedian: `liftMedianValue`,
  CohortSize: `cohortSize`,

  Total: `totalValue`,
  TimeComparisonTotal: `tcTotalValue`,

  DatePointPrefix: `datePoint-`,
  TimeComparisonDatePointPrefix: `tcDatePoint-`,
  LiftComparisonDatePointPrefix: `liftDatePoint-`,

  LiftComparisonExtraFieldPrefix: `liftExtraDatePoint-`,

  SegmentPrefix: `$mp-segment-`,
  MetricPrefix: `$mp-metric-`,
};

export const AggregationTypeConfig: Record<
  AggregationType,
  {
    title: string;
    pastTitle: string;
    field: string;
    tcField: string;
    liftField: string;
  }
> = {
  [AggregationType.Average]: {
    title: `Average`,
    pastTitle: `Past Avg.`,
    field: CommonDataTableFields.Average,
    tcField: CommonDataTableFields.TimeComparisonAverage,
    liftField: CommonDataTableFields.LiftAverage,
  },
  [AggregationType.Sum]: {
    title: `Sum`,
    pastTitle: `Past Sum`,
    field: CommonDataTableFields.Sum,
    tcField: CommonDataTableFields.TimeComparisonSum,
    liftField: CommonDataTableFields.LiftSum,
  },
  [AggregationType.Min]: {
    title: `Min`,
    pastTitle: `Past Min`,
    field: CommonDataTableFields.Min,
    tcField: CommonDataTableFields.TimeComparisonMin,
    liftField: CommonDataTableFields.LiftMin,
  },
  [AggregationType.Max]: {
    title: `Max`,
    pastTitle: `Past Max`,
    field: CommonDataTableFields.Max,
    tcField: CommonDataTableFields.TimeComparisonMax,
    liftField: CommonDataTableFields.LiftMax,
  },
  [AggregationType.Median]: {
    title: `Median`,
    pastTitle: `Past Med.`,
    field: CommonDataTableFields.Median,
    tcField: CommonDataTableFields.TimeComparisonMedian,
    liftField: CommonDataTableFields.LiftMedian,
  },
};

/** Maps a data table field name (e.g., 'sumValue', 'liftSumValue', 'tcSumValue') to its AggregationType */
export function getAggregationTypeFromField(field: string): AggregationType | null {
  for (const [type, config] of Object.entries(AggregationTypeConfig)) {
    if (field === config.field || field === config.liftField || field === config.tcField) {
      return type as AggregationType;
    }
  }
  return null;
}

/**
 * Low level table style options. Should be considered package private until they stabilize.
 *
 * If you need a change for a new context, add a Theme instead.
 */
export type ThemeSpec = {
  dataCellBorders: `line` | `gap`;
  cellBorderHover: `none` | `outline`;
  /** whether data cells in the table have are clickable and focusable */
  cellFocusable: boolean;
  cellHeaderBorders: `cell` | `column-group` | `none`;
  cellBackground: `focus-on-hover` | `heatmap` | `none`;
  /** Explicit height in pixels for rows after the header row. This is required because without explicit row heights
   * the horizontal scrollbar (if one exists) will overlap the last row. */
  rowHeight: number;
  /** Whether to render checkboxes. */
  selectableRows: boolean;
  /** Use the new segment block design (no background) */
  multimetricStyles: boolean;
};

/** Column width overrides (from resizing) in pixels */
export type ColumnWidthOverrides = {
  [columnIdx: number]: number;
};

export type ColumnResizeDetail = {
  columnWidthOverrides: ColumnWidthOverrides;
};

export type ColumnResizeEvent = CustomEvent<ColumnResizeDetail>;

export type GroupHeader = {
  title: string;
  span: number;
  /** Prepends the step number label for groups that represent a funnel step */
  funnelStepNumber?: number;
};

export enum TruncatableTextType {
  Default = `default`,
  HeaderTitle = `header_title`,
  HeaderSubtitle = `header_subtitle`,
}

export interface TableState {
  /** Table's rows to display, could be any array of objects.
   * Note: For performance reasons, rows/filteredRows/flatFilteredRows share the same underlying row objects (rather
   * than copies). */
  rows: Array<TableRow<TableCell>>;
  segmentMetadata?: SegmentMetadataNode;
  /** Subset of rows after applying the searchFilter. */
  filteredRows: Array<TableRow<TableCell>>;
  /** Flattened version of filteredRows (expanded `groupedRows` are pulled up into the array). Will also have fields
   * like `rowHeight`/`offsetTop` computed for the rows for rendering purposes */
  flatFilteredRows: Array<TableRow<TableCell>>;
  sorting: SortConfig;
  /** Whether the user has scrolled the body, so we can set the scroll shadow for titles and make it look sticky */
  hasBodyScrolled: boolean;
  columns: TableColumn[];
  stickyColumnsCount: number;
  columnWidthOverrides: ColumnWidthOverrides;
  resizeColumnIndex: number;
  /** The table's width in pixels. Necessary because div.scrollableColumns needs an explicit width
   * for horizontal scroll to work */
  tableWidth: number;
  /** The context for the row that is being hovered over. */
  hoveredCellContext: CellRenderContext;
  /** Is the select all checkbox being hovered over */
  isSelectAllHovered: boolean;
  /** If the hovered text is truncated, this is set to the type of text it is */
  hoveredTruncatedTextType: TruncatableTextType;
  /** Headers for column groups, extracted to state when columnGroups are set. Will be null if group headers aren't
   * present in the column groups. */
  groupHeaders: GroupHeader[] | null;
  searchFilter: string;
  segmentCellBlocks: SegmentCellBlock[];
  /** The scrollable container of the table. This element is always used for horizontal scroll. It's used for vertical
   * in table scroll mode. */
  scrollEl: HTMLElement;
  scrollParentEl: HTMLElement;
  /** Top-most row to render */
  renderRowStartIdx: number;
  /** Number of rows to render */
  numRowsToRender: number;
  /** Left-most data column to render */
  renderDataColumnStartIdx: number;
  /** Number of data columns to render */
  numDataColumnsToRender: number;
  /** Represents an ordered list of row/column heights/widths and the count of each row with that height.
   * E.g. [{count: 1, height: 10},{count: 2, height: 20},{count: 1, height 10}] Represents 4 rows: 10, 20, 20, 10
   */
  rowHeightsMatrix: Array<{count: number; height: number}>;
  dataColumnWidthsMatrix: Array<{count: number; width: number}>;
  openViewNMenuSegmentIdx?: number;
  /** The currently selected aggregation type for the summary column */
  aggregationType: AggregationType;
}

/** A grouping of TableColumns */
export interface ColumnGroup<Row = any, ColumnContext = any> {
  /** Columns in this group */
  columns: Array<TableColumn<Row, ColumnContext>>;

  /** Header title for the group of columns. Only applicable for non-sticky column groups. */
  title?: string;

  /** Prepends the step number label for groups that represent a funnel step */
  funnelStepNumber?: number;
}

type SortTransformOptionalProps = {
  sortOrder: number;
  colSortConfig?: FlatSortConfig;
  path?: string;
};

export type SortTransformFunc<Row = any> = (
  row: Row,
  field: string,
  props?: SortTransformOptionalProps,
) => number | Date;

export type CellRendererFuncProps<Row> = {
  item: Row;
  context: BodyCellRenderContext;
};

/**
 * used to cache expensive computations that only need to be done once per render.
 */
export type TableStats = {
  hasColumnHeaderWithSubtitle: boolean;
  numHeaderRows: number;
  showSegmentCellBlocks: boolean;
};

export type CellRendererFunc<Row> = (props: CellRendererFuncProps<Row>) => React.ReactNode | VNode | string;

/** Type of field, affects the cell font and other styling-related changes. */
export enum TableColumnType {
  /** segment column (even if there's no breakdown, the first column is considered the segment column) */
  Segment = `segment`,
  /** data column */
  Data = `data`,
}

export type SegmentColumnSpec = {
  title?: string;
  propertyName?: string;
  segmentType: PropertyType;
  breakdownIdx?: number;
};

type TableColumnBase<Row = any, ColumnContext = any> = {
  /** The property inside each item that corresponds to the header */
  field: string;
  /** Optional text before title with different styling */
  titlePrefix?: string;
  /** Title for the column header */
  title?: Translatable;
  /** Subtitle to be displayed below the column header */
  subtitle?: string;
  /** icon to show in place of a title */
  icon?: string;

  type: TableColumnType;

  /** Suggested width, in pixels, of the column. This width is used as a hint, but the actual `computedWidth` might be
   * different unless `fixedWidth` is true. A default is used if this is omitted */
  initialWidth?: number;
  /** (internal) The actual rendered width of the columns, accounting for resized columns and growing columns to fit
   * the width of the table */
  computedWidth?: number;
  /** (internal) the left offset of this column relative to the first column */
  offsetLeft?: number;
  /** true if the column's width should not grow to fit the table width (for e.g. checkbox column) */
  fixedWidth?: boolean;
  /** true if the column is resizable by the user (e.g. segment columns). By default, sticky columns are resizable */
  resizable?: boolean;
  /** Minimum width that the column can be resized to in pixels. A default is used if this is omitted */
  resizeMinWidth?: number;
  // TODO(mack): Should remove this field and use TableColumnType to determine whether it's or not it's a sticky column.
  /** whether this column should be pinned to the left */
  sticky?: boolean;
  /** text color for data cells. lower in priority than cells[] */
  textColor?: string;
  /** background color for data cells. lower in priority than cells[] */
  backgroundColor?: string;

  /** (internal) The global column idx */
  columnIdx?: number;
  /** (internal) The column idx within the fixed/data subset. For example, the first data column will
   * have subsetColumnIdx=0 */
  subsetColumnIdx?: number;
  /** (internal) whether this column is the last of its column group and should have border */
  last?: boolean;
  /** (internal) whether this column is the first of its column group.  */
  first?: boolean;
  /** (internal) true if this is the "first first" column (first column in the first column group) */
  leftmost?: boolean;
  /** (internal) true if this is the "last last" column (last column in the last column group) */
  rightmost?: boolean;
  /** (internal) whether this column is the last sticky column */
  lastSticky?: boolean;

  /** tooltip to give more info on column title, no (i) */
  tooltip?: Translatable;
  /** tooltip alternative to the helptext */
  label?: string;
  /** if the cells can be clicked, hovered or focused */
  isSelectable?: boolean;
  /** If the field can be sorted by clicking on the column header */
  isSortable?: boolean;
  /** Title to show in grouped sorting menu. Columns with this property will be included in the menu. */
  sortSelectTitle?: Translatable;
  /** A custom renderer function (usally $mixins[`cell-{field}`]) */
  cellRenderer?: CellRendererFunc<Row>;
  /** A custom function that returns a number for sort comparison */
  sortTransform?: SortTransformFunc<Row>;
  /** A custom function that returns a label for display e.g 1234 -> 1,234 */
  labelTransform?: (row: Row, field: string) => string;
  /** Parallel to `field`, used to display lift pills in the same context as the regular field */
  liftField?: string;
  /** Similar to `liftField`, used to pass in extra data used for tooltips **/
  liftExtra?: string;
  /** allows columns to define additional data passed down for rendering, or click events */
  context?: ColumnContext | undefined;
  /** (internal) render the checkbox in this column */
  hasCheckbox?: boolean;
  /** (internal) render the collapse rows button in this column (used in retention curve) */
  hasCollapseButton?: boolean;
  /** Only present if this is a compare-to column. Used to determine how the column is displayed. */
  timeComparisonMetadata?: {
    timeComparisonColumnType: TimeComparisonColumnType;
    dateRange: AbsoluteDateRange;
  };
};

export type SegmentColumn<Row = any, ColumnContext = any> = TableColumnBase<Row, ColumnContext> & {
  type: TableColumnType.Segment;
  contentType: PropertyType;
  /** if segment label is prefixed with A., B., C., ... BA. (via getAlphaLabelFromIndex) that should be considered for sorting  */
  hasOrdinalPrefix?: boolean;
  /** (internal) if this is a segment column, the segment index */
  segmentIndex?: number;
  /** (internal) the number of unique segment values */
  segmentCardinality?: number;
  /** (internal) true if this column is the last segment column */
  lastSegment?: boolean;
  /** if this segment is mapped to a particular breakdown in the query */
  breakdownIdx?: number;
};

export enum TableDataType {
  /** text columns that aren't one of the segment columns */
  Text = `text`,
  /** mono font on data cells. */
  Number = `number`,
}

export enum DataFormatType {
  Number = `number`,
  Decimal = `decimal`,
  Percent = `percent`,
  Time = `time`,
}

export type DataColumn<Row = any, ColumnContext = any> = TableColumnBase<Row, ColumnContext> & {
  type: TableColumnType.Data;
  contentType: TableDataType;
  dataFormatType?: DataFormatType;
  /** (internal) the overall value for multi-metric columns */
  metricOverall?: number;
  liftOverall?: number;
  comparisonOverall?: number;
};

export const isTimeComparisonColumnType = (col: TableColumn | ColumnGroup): col is TableColumn =>
  `timeComparisonMetadata` in col;

export type TableColumn<Row = any, ColumnContext = any> =
  | SegmentColumn<Row, ColumnContext>
  | DataColumn<Row, ColumnContext>;

export type Columns<Row = any, ColumnContext = any> = Array<
  TableColumn<Row, ColumnContext> | ColumnGroup<Row, ColumnContext>
>;

export interface TableCell {
  /** numeric value that this cell contains */
  value: number;
  /** the display string of the value this cell contains */
  displayValue: string;
  /** the background color of this cell */
  backgroundColor?: string;
  /** the text color of this cell */
  textColor?: string;
  /** icon if this cell has one */
  icon?: string;
}

/** TableRows can specify their cells via "[column field]: [row value]" key-value pairs on the TableRow,
 * or via the `cells` property (see comment on `cells` property below).
 *
 * The current key-value approach in e.g. funnels isn't great because it often requires passing a
 * "sortTransform" function with the column to produce a sortable value, instead of just providing a
 * separate "label" and "value"
 */
export interface TableRow<T extends Record<string, any> = any> {
  /** Identifier for given row */
  identifier: string;
  /** Whether the row was newly added by user via a create action */
  isNew?: boolean;
  /** Whether the row should be shown selected. Applies to multi-select tables */
  isSelected?: boolean;
  /** Whether the row is selectable (will disable checkboxes) */
  isSelectDisabled?: boolean;
  /** A string to be displayed via a tooltip when selection is disabled */
  selectDisabledReason?: string;
  /** If this row is selected, override the checkmark color. */
  checkedColor?: string;
  checkedIcon?: string;
  /**
   * To specify the values for the columns in the rows, you can either have them as fields on the TableRow, or you can
   * pass them in the `cells` field. By using the cells field, you can specify different styling information for each
   * cell (e.g. background color, text color). This is currently used in Retention. When using `cells` to specify the
   * cell values, you should provide `cellRenderer` functions for the columns that reads the value from the associated
   * cell.
   */
  cells?: T[];
  /** background color for a specific line*/
  backgroundColor?: string;
  /** rows that are grouped to this row. This row is parent row */
  groupedRows?: Array<TableRow<T>>;
  /** Whether the grouped rows should be collapsed */
  isCollapsed?: boolean;

  /** (internal) The index of this row in `this.state.rows` */
  rowIdx?: number;
  /** (internal) If this is a grouped row, the index of this row within `parentRow.groupedRows` */
  groupedRowIdx?: number;
  /** (internal) If it's a comparison row. Only used by Funnels Steps */
  isComparisonRow?: boolean;

  /** (internal) indexes of the row's segment cells that are overlaid by the start of a segment block */
  segmentBlockStarts?: Set<number>;
  /** (internal) indexes of the row's segment cells that are overlaid by the end of a segment block */
  segmentBlockEnds?: Set<number>;
  /** (internal) whether this row is the start of any segment block */
  segmentStartRow?: boolean;
  /** (internal) whether this row is the end of any segment block */
  segmentEndRow?: boolean;

  /** (internal) For filtered rows, the index of this row among filtered rows  */
  filteredRowIdx?: number;
  /** (internal) For rendered rows, the index of this row among rendered rows */
  renderRowIdx?: number;
  /** (internal) for flattened filtered rows, the top offset of this row relative to the first row */
  offsetTop?: number;
  /** (internal) the rendered height of this row */
  rowHeight?: number;
}

export enum RowType {
  Body = `body`,
  Header = `header`,
  GroupHeader = `groupHeader`,
  TimeComparisonHeader = `timeComparisonHeader`,
}

export type CellCoordinates = {
  columnIdx: number;
  rowIdx: number;
  /**
   * Each row can have a list of grouped rows underneath it (this is used in Retention curve table, where the main row
   * is the segment and the grouped rows are for individual dates.
   */
  groupedRowIdx?: number;
};

export interface BodyCellRenderContext {
  rowType: RowType.Body;
  field: string;
  label: string;
  isComparison: boolean;
  columnIdx: number;
  rowIdx: number;
  groupedRowIdx?: number;

  /** Lift pills will be rendered in the same context as the regular field, just next to it */
  liftField?: string;
  liftExtra?: string;
  liftType?: LiftType;

  /** true if this cell is the first in a segment cell block (value will be used as the label via z-index) */
  segmentStartCell?: boolean;
  /** true if this cell is the last in a segment cell block */
  segmentEndCell?: boolean;
}

export interface HeaderCellRenderContext {
  rowType: RowType.Header;
  columnIdx: number;
}

export interface GroupHeaderCellRenderContext {
  rowType: RowType.GroupHeader;
  /** columnIdx refers to the start column of the group header */
  columnIdx: number;
}

export interface TimeComparisonHeaderCellRenderContext {
  rowType: RowType.TimeComparisonHeader;
  columnIdx: number;
}

export type CellRenderContext =
  | BodyCellRenderContext
  | HeaderCellRenderContext
  | GroupHeaderCellRenderContext
  | TimeComparisonHeaderCellRenderContext;

export type CellClickEvent<Row extends Record<string, any> = TableCell, ColumnContextType = any> = CustomEvent<{
  row: TableRow<Row>;
  field: TableColumn[`field`];
  bgColor: string;
  textColor: string;
  context: BodyCellRenderContext;
  modifierKeys: ModifierKeys;
  target: HTMLElement;
  mouseX: number;
  mouseY: number;
  column: TableColumn<Row, ColumnContextType>;
}>;

export type SelectionChangeEvent<T extends TableRow<any> = TableRow<TableCell>> = CustomEvent<{
  changed: Array<{isSelected: boolean; row: T}>;
  /** Whether this selection change was caused by the toggle-all button. */
  isToggleAll?: boolean;
  isSearchMode?: boolean;
  rows: T[];
}>;

export type CollapseChangeEvent<T extends TableRow<any> = TableRow<TableCell>> = CustomEvent<{
  isCollapsed: boolean;
  row: T;
}>;

export type SortChangeEvent<T = TableRow<TableCell>> = CustomEvent<{
  // TODO: Make sorting mandatory once trend data table is GA
  sorting?: SortConfig;
  /** current table rows, sorted */
  rows?: T[];
}>;

export type ViewNToggleDetail = {
  isViewNMode: boolean;
};

export type ViewNToggleEvent = CustomEvent<ViewNToggleDetail>;

//#region multi-sort
export type ColSortOptionValue = {segmentIndex: number; sort: FlatSortConfig};

/** Summary data for each segment, e.g.: {Chrome: {sum: 12, children: {USA: {sum: 6}, India: {sum: 6}}}, Safari: ...} */
export type SegmentStats = {
  [segmentValue: string]: {
    sum: number;
    children?: SegmentStats;
  };
};

export type SegmentCellBlock = {
  /** index of the first flattened row that's part of this segment block */
  flatRowStartIdx: number;
  /** index of the last (exclusive) flattened row that's part of this segment block */
  flatRowEndIdx: number;
  /** The index of the column for this segment block */
  columnIdx: number;
  /** Index of the starting row in `state.rows`. This differs from `flatRowStartIdx` when there are expanded sub-rows
   * (which are rendered in the grid but not included in the rows collection) */
  rowStartIdx: number;
  rowEndIdx: number;
  label: string;
  metricMetadatas?: SegmentMetricMetadata[];
};
//#endregion

export enum TrackName {
  InsightsLine = `insights-line-table`,
  InsightsColumn = `insights-column-table`,
  InsightsMetric = `insights-metric-table`,
  InsightsPie = `insights-pie`,
  InsightsTable = `insights-table-chart`,
  FunnelsSteps = `funnels-table`,
  FunnelsStepsMetric = `funnels-metric-table`,
  FunnelsTrends = `funnels-trends-table`,
  FunnelsMedianMetric = `funnels-median-metric`,
  RetentionCurve = `retention-table`,
  RetentionTrends = `retention-trends-table`,
  RetentionTrendsMetric = `retention-trends-metric`,
}

/** Key is the field that is group sortable, value is column fields to add (at least sortSelectTitle) */
export type GroupSortableColumnsDictionary = {
  [key: string]: Required<Pick<DataColumn, `sortSelectTitle`>> & Partial<DataColumn>;
};

export type BodyRowHoverEvent = CustomEvent<{
  rowId: string;
}>;

export enum TreeNodeType {
  Leaf = `leaf`,
  Parent = `parent`,
}

type BaseTreeNode = {
  label: string;
};
type LeafTreeNode<ExtraData> = ExtraData &
  BaseTreeNode & {
    type: TreeNodeType.Leaf;
  };
type ParentTreeNode<ExtraLeafData, ExtraParentData> = ExtraParentData &
  BaseTreeNode & {
    type: TreeNodeType.Parent;
    children: Array<TreeNode<ExtraLeafData, ExtraParentData>>;
  };
// Need to use {} rather than EmptyObj since type intersection with EmptyObj doesn't work
// eslint-disable-next-line @typescript-eslint/no-restricted-types, @typescript-eslint/no-empty-object-type
type TreeNode<ExtraLeafData = {}, ExtraParentData = {}> =
  | LeafTreeNode<ExtraLeafData>
  | ParentTreeNode<ExtraLeafData, ExtraParentData>;

type PartialSegmentCellBlock = Partial<SegmentCellBlock>;
export type SegmentBlockNode = TreeNode<PartialSegmentCellBlock & {row: TableRow}, PartialSegmentCellBlock>;
export type ParentSegmentBlockNode = Extract<SegmentBlockNode, {type: TreeNodeType.Parent}>;

export type SegmentNode = TreeNode<{row: TableRow}>;
export type ParentSegmentNode = Extract<SegmentNode, {type: TreeNodeType.Parent}>;

export type SegmentMetricMetadata = {
  metricOverall: number;
  liftOverall?: number;
  comparisonOverall?: number | null;
};

type SegmentMetadata = {
  metricMetadatas: SegmentMetricMetadata[];
};
export type SegmentMetadataNode = TreeNode<SegmentMetadata, SegmentMetadata>;
export type ParentSegmentMetadataNode = Extract<SegmentMetadataNode, {type: TreeNodeType.Parent}>;

export type InitialColResizeState = {
  x: number;
  handle: {el: HTMLElement; left: number};
  leftCol: {idx: number; col: TableColumn; minWidth: number; deltaMin: number};
  rightCol: {idx: number; col?: TableColumn; minWidth: number};
};

export enum TimeComparisonColumnType {
  Past = `past`,
  Current = `current`,
}
